import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCoordinatedOverlay } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { Popover } from '#pplx/frontend/aether/components/Popover/index.js';
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { useSourcesMenuConfig } from '#pplx/frontend/apps/ask/ask-input/ask-input-sources-menu/components/SourcesMenuConfigContextSupport.js';
import { PremiumSourceTooltipContent } from '#pplx/frontend/apps/ask/sources/components/PremiumSourceTooltipContent.js';
import { useSourceLimits } from '#pplx/frontend/apps/ask/sources/hooks/useSourceLimits.js';
import { useSourceMeta } from '#pplx/frontend/apps/ask/sources/hooks/useSourceMeta.js';
import { InlinePremiumSourceBadge } from '#pplx/frontend/components/badges/PremiumSourceBadge.js';
import { PREMIUM_SOURCE_BADGE_LABEL_MESSAGE, PREMIUM_SOURCE_BADGE_SUBTITLE_MESSAGE, } from '#pplx/frontend/components/badges/PremiumSourceBadgeMessages.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { noop } from 'lodash-es';
import React, { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
export const SourcesMenuItemPremiumData = React.memo(function SourcesMenuItemPremiumData({ source, enabled, onToggle }) {
    const { isOpen, open, close } = useCoordinatedOverlay();
    const { label, getIcon } = useSourceMeta({ sourceId: source });
    const { getSourceLimit } = useSourceLimits();
    const { hideDescriptionOverlays } = useSourcesMenuConfig();
    const intl = useIntl();
    const timer = useRef(null);
    const premiumBadgeLabel = intl.formatMessage(PREMIUM_SOURCE_BADGE_LABEL_MESSAGE);
    const handleMouseEnter = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        open();
    }, [open]);
    const handleMouseLeave = useCallback(() => {
        timer.current = setTimeout(() => {
            close();
        }, 300);
    }, [close]);
    const disabled = useMemo(() => {
        const { remaining } = getSourceLimit(source);
        return remaining === 0;
    }, [getSourceLimit, source]);
    const labelContent = useMemo(() => (_jsxs("span", { className: "inline-flex items-center gap-1", children: [label, hideDescriptionOverlays ? (_jsx(Tooltip, { content: premiumBadgeLabel, subtitle: intl.formatMessage(PREMIUM_SOURCE_BADGE_SUBTITLE_MESSAGE), side: "right", children: _jsx("span", { className: "inline-flex", tabIndex: 0, children: _jsx(InlinePremiumSourceBadge, { ariaLabel: premiumBadgeLabel }) }) })) : (_jsx(InlinePremiumSourceBadge, { ariaLabel: premiumBadgeLabel }))] })), [hideDescriptionOverlays, intl, label, premiumBadgeLabel]);
    const triggerElement = useMemo(() => {
        const leadingAccessory = getIcon('small');
        return (_jsx("div", { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: _jsx(DropdownMenu.CheckboxItem, { leadingAccessory: leadingAccessory, checked: enabled, onCheckedChange: onToggle, disabled: disabled, children: labelContent }) }));
    }, [disabled, enabled, handleMouseEnter, handleMouseLeave, getIcon, labelContent, onToggle]);
    if (!label) {
        logger.warn(`SourcesMenuItemPremiumData: No label found for source ${source}. Cannot render component.`);
        return null;
    }
    if (hideDescriptionOverlays) {
        return triggerElement;
    }
    return (_jsx(Popover, { interaction: "hover", appearance: "dark", triggerElement: triggerElement, side: "right", open: isOpen, 
        // There is a bug in radix hover card where it loses track of events due to menu items being the trigger
        // so we need to manually control the open state
        onOpenChange: noop, children: _jsx("div", { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: _jsx(PremiumSourceTooltipContent, { source: source }) }) }));
});
//# sourceMappingURL=SourcesMenuItemPremiumData.js.map